/**
 * MODELOS DE PROMOÇÃO DA SERCAMP — as formas pelas quais um colaborador pode
 * ser promovido.
 *
 * Cada modelo é um CAMINHO diferente: um eletricista que vira Pleno (progressão
 * de nível) não passa pelo mesmo crivo de quem assume uma equipe (liderança) ou
 * de quem foi contratado em experiência e está sendo efetivado. Registrar POR
 * QUAL modelo a promoção aconteceu é o que permite ao RH explicar depois por
 * que fulano subiu — e cobrar o mesmo critério de todo mundo.
 *
 * O catálogo fica no FRONT porque é texto de política, não dado do banco: o que
 * é gravado no registro da promoção é o NOME do modelo (coluna `modelo`) e os
 * critérios que foram marcados (coluna `criterios`). Assim, mudar a redação de
 * um critério aqui não invalida o histórico já registrado.
 */

export interface ModeloPromocao {
  /** Chave interna (não aparece na tela). */
  key: string;
  /** Nome gravado na promoção. */
  nome: string;
  /** Uma linha explicando quando usar este caminho. */
  resumo: string;
  /** O que muda no cadastro do colaborador. */
  efeito: string;
  /** Reajuste que a empresa costuma praticar neste modelo. */
  reajuste: string;
  /** Tempo mínimo antes de usar o modelo. */
  carencia: string;
  /** O que precisa estar cumprido — vira a marcação do registro. */
  criterios: string[];
  /** O que precisa estar anexado/registrado (e em qual aba do sistema). */
  evidencias: string[];
  /** Quem assina. */
  aprovacao: string;
}

export const MODELOS_PROMOCAO: ModeloPromocao[] = [
  {
    key: "vertical",
    nome: "Promoção vertical (mudança de cargo)",
    resumo:
      "O colaborador assume um cargo acima na estrutura, com mais responsabilidade e autonomia.",
    efeito: "Muda o cargo (e o nível) no cadastro; o salário passa para o valor do novo cargo.",
    reajuste: "Valor do cargo de destino na tabela de Cargos e Salários",
    carencia: "12 meses no cargo atual",
    criterios: [
      "Existe a vaga (alguém saiu, a equipe cresceu ou a posição foi criada)",
      "Cumpriu o tempo mínimo de 12 meses no cargo atual",
      "Última avaliação de desempenho igual ou acima de 7,0",
      "Executa com autonomia tudo o que o cargo atual exige",
      "Já cumpre parte das atividades do cargo de destino",
      "Sem advertência ou suspensão nos últimos 12 meses",
      "Parecer favorável do gestor da área",
    ],
    evidencias: [
      "Avaliação de desempenho (aba Avaliações)",
      "Parecer do gestor no campo Justificativa",
      "Cargo de destino cadastrado em Cargos e Salários",
    ],
    aprovacao: "Gestor da área → RH → Diretoria",
  },
  {
    key: "nivel",
    nome: "Progressão de nível (Júnior → Pleno → Sênior)",
    resumo:
      "Mesmo cargo, senioridade maior: a pessoa passa a fazer o mesmo serviço com mais autonomia e complexidade.",
    efeito: "Mantém o cargo e sobe o nível; o salário vai para o valor daquele nível na tabela.",
    reajuste: "Diferença entre os níveis na tabela de Cargos e Salários",
    carencia: "12 meses de Júnior para Pleno · 24 meses de Pleno para Sênior",
    criterios: [
      "Cumpriu o tempo mínimo no nível atual",
      "Executa os serviços do nível sem precisar de acompanhamento",
      "Assume atividades de maior complexidade (ensaios, comissionamento, obras maiores)",
      "Orienta os colegas mais novos no campo",
      "Avaliação de desempenho igual ou acima de 7,0",
      "Treinamentos obrigatórios do cargo em dia",
      "Sem ocorrência disciplinar nos últimos 12 meses",
    ],
    evidencias: [
      "Treinamentos e certificados na pasta do colaborador",
      "Avaliação de desempenho do último ciclo",
    ],
    aprovacao: "Gestor da área → RH",
  },
  {
    key: "merito",
    nome: "Promoção por mérito (desempenho)",
    resumo:
      "Reconhecimento de quem entrega acima do combinado, sem depender de vaga aberta.",
    efeito: "Reajuste de mérito; pode manter o mesmo cargo e nível.",
    reajuste: "De 5% a 10% do salário atual",
    carencia: "12 meses desde o último reajuste de mérito",
    criterios: [
      "Avaliação de desempenho igual ou acima de 9,0 (ou dois ciclos seguidos acima de 8,0)",
      "Resultado concreto que dá para mostrar (prazo, retrabalho, economia, cliente satisfeito)",
      "Assumiu responsabilidade além do que o cargo pede",
      "Não há reajuste de mérito nos últimos 12 meses",
      "Cabe no orçamento da área",
    ],
    evidencias: [
      "Avaliações do ciclo (aba Avaliações)",
      "Descrição do resultado no campo Justificativa",
    ],
    aprovacao: "Gestor da área → RH → Diretoria",
  },
  {
    key: "tecnica",
    nome: "Promoção técnica (certificação / habilitação)",
    resumo:
      "A pessoa se habilitou para um serviço que antes não podia executar — e isso muda o que ela vale em campo.",
    efeito: "Enquadramento técnico: libera atividades novas e ajusta o salário ao cargo habilitado.",
    reajuste: "De 5% a 15%, conforme a habilitação conquistada",
    carencia: "Não tem — vale a partir do certificado válido",
    criterios: [
      "Certificado válido e dentro do prazo de reciclagem",
      "Habilitação exigida pelo serviço (NR-10, NR-10 SEP, NR-35, NR-33, NR-12, técnico em eletrotécnica, CNH da categoria)",
      "Competência já marcada no cadastro do colaborador",
      "Aplicou a habilitação em serviço, acompanhado, pelo menos uma vez",
      "Exame médico compatível com a atividade em dia (ASO)",
    ],
    evidencias: [
      "Certificado anexado na aba Treinamentos ou na pasta do colaborador",
      "Competências marcadas no cadastro (campo Skills)",
    ],
    aprovacao: "Gestor da área → Segurança do Trabalho → RH",
  },
  {
    key: "lideranca",
    nome: "Promoção a liderança (encarregado, líder de equipe, gestor)",
    resumo:
      "Passa a responder por gente, e não só pelo próprio serviço. É o caminho mais exigente.",
    efeito: "Cargo de liderança (nível Liderança na tabela), com equipe sob responsabilidade.",
    reajuste: "Valor do cargo de liderança na tabela de Cargos e Salários",
    carencia: "24 meses de casa",
    criterios: [
      "Já conduz a equipe em campo na prática",
      "Avaliação de desempenho igual ou acima de 8,0",
      "Conduta em segurança exemplar — sem acidente com responsabilidade nos últimos 24 meses",
      "Sabe preencher e cobrar a documentação da obra (APR, checklist, relatório)",
      "Passou por treinamento de liderança ou vai passar em até 90 dias",
      "Relacionamento com o cliente sem ocorrência registrada",
      "Aval da Diretoria",
    ],
    evidencias: [
      "Avaliações do último ciclo",
      "Treinamento de liderança (aba Treinamentos)",
      "Parecer do gestor no campo Justificativa",
    ],
    aprovacao: "Gestor da área → RH → Diretoria",
  },
  {
    key: "lateral",
    nome: "Movimentação lateral (mudança de área)",
    resumo:
      "Troca de área sem subir de nível — aproveita quem já é de casa numa função que combina mais com o perfil.",
    efeito: "Muda o cargo e o departamento; o salário costuma ser mantido.",
    reajuste: "Em regra sem reajuste (só se o cargo de destino tiver valor maior na tabela)",
    carencia: "6 meses na área atual",
    criterios: [
      "Existe vaga na área de destino",
      "O colaborador quer a mudança (pedido registrado)",
      "Tem as competências que a área de destino exige, ou consegue aprender em até 90 dias",
      "Gestor de origem e gestor de destino de acordo",
      "Período de adaptação de 90 dias combinado com as duas partes",
    ],
    evidencias: ["Aceite dos dois gestores no campo Justificativa"],
    aprovacao: "Gestor de origem → Gestor de destino → RH",
  },
  {
    key: "enquadramento",
    nome: "Enquadramento salarial (correção de desvio de função)",
    resumo:
      "Não é prêmio: é acerto. O que a pessoa faz hoje não bate com o cargo registrado, ou o salário está abaixo da tabela.",
    efeito: "Corrige o cargo e/ou o salário para o que a tabela de Cargos e Salários define.",
    reajuste: "O necessário para chegar ao valor do cargo na tabela",
    carencia: "Não tem — quanto antes, menor o risco trabalhista",
    criterios: [
      "As atividades do dia a dia não são as do cargo registrado",
      "O salário está abaixo do valor do cargo/nível na tabela",
      "A diferença foi conferida com a tabela de Cargos e Salários",
      "Parecer do RH sobre o risco de manter como está",
    ],
    evidencias: [
      "Comparação com a aba Cargos e Salários",
      "Descrição das atividades reais no campo Justificativa",
    ],
    aprovacao: "RH → Diretoria",
  },
  {
    key: "efetivacao",
    nome: "Efetivação (fim da experiência, aprendiz ou estágio)",
    resumo:
      "O contrato temporário vira efetivo. É a promoção mais comum e a que mais escapa do registro.",
    efeito: "Passa a efetivo no cargo; o salário sai do valor de experiência para o do cargo.",
    reajuste: "Valor do cargo na tabela de Cargos e Salários",
    carencia: "90 dias de experiência (ou o fim do contrato de aprendizagem/estágio)",
    criterios: [
      "Cumpriu o período de experiência (45 + 45 dias) ou o contrato de aprendiz/estágio",
      "Avaliação do gestor ao fim da experiência foi favorável",
      "Integração e treinamentos obrigatórios concluídos",
      "Documentação completa no DP",
      "Frequência sem falta não justificada no período",
    ],
    evidencias: [
      "Avaliação de experiência (aba Avaliações)",
      "Documentos do colaborador (DP)",
    ],
    aprovacao: "Gestor da área → RH → DP",
  },
  {
    key: "tempo_casa",
    nome: "Progressão por tempo de casa",
    resumo:
      "Reconhece quem se mantém firme no mesmo nível, entregando com regularidade, ano após ano.",
    efeito: "Reajuste de progressão dentro do mesmo cargo e nível.",
    reajuste: "De 3% a 5% do salário atual",
    carencia: "24 meses no mesmo nível",
    criterios: [
      "Completou 24 meses no mesmo cargo e nível",
      "Avaliações do período iguais ou acima de 7,0",
      "Nenhuma ocorrência disciplinar no período",
      "Treinamentos obrigatórios e reciclagens em dia",
    ],
    evidencias: ["Histórico de avaliações", "Treinamentos do período"],
    aprovacao: "RH → Diretoria",
  },
  {
    key: "sucessao",
    nome: "Plano de sucessão (posição-chave)",
    resumo:
      "Preparado com antecedência para assumir a posição de alguém que vai sair, se aposentar ou mudar de área.",
    efeito: "Assume a posição mapeada, com acompanhamento do ocupante antes da troca.",
    reajuste: "Valor da posição de destino, aplicado quando a troca acontece",
    carencia: "Depende do plano — em regra, de 6 a 12 meses de preparação",
    criterios: [
      "A posição está mapeada como chave para a operação",
      "O sucessor foi indicado formalmente e sabe disso",
      "Plano de desenvolvimento em andamento (treinamentos e acompanhamento)",
      "Período de sombra junto ao ocupante atual cumprido",
      "Data prevista da troca definida",
    ],
    evidencias: [
      "Plano de desenvolvimento (aba Treinamentos)",
      "Data prevista e etapas no campo Justificativa",
    ],
    aprovacao: "Diretoria → RH",
  },
];

/** Acha o modelo pelo nome gravado na promoção (o que vai para o banco). */
export function modeloPorNome(nome: unknown): ModeloPromocao | null {
  const alvo = String(nome ?? "").trim().toLowerCase();
  if (!alvo) return null;
  return MODELOS_PROMOCAO.find((m) => m.nome.toLowerCase() === alvo) ?? null;
}
